import React, { useEffect, useState, useRef } from "react";

// --- Helper: Format Date ---
const toDateKey = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return null;
};

// --- Helper: Unique Color Assignment ---
const getColorPalette = (Cesium) => [
  Cesium.Color.CYAN, Cesium.Color.YELLOW, Cesium.Color.MAGENTA, 
  Cesium.Color.LIME, Cesium.Color.ORANGE, Cesium.Color.HOTPINK,
  Cesium.Color.TEAL, Cesium.Color.CORAL, Cesium.Color.AQUA,
  Cesium.Color.GOLD, Cesium.Color.CHARTREUSE, Cesium.Color.CRIMSON,
  Cesium.Color.DARKORANGE, Cesium.Color.DEEPPINK, Cesium.Color.LAWNGREEN,
  Cesium.Color.MEDIUMSPRINGGREEN, Cesium.Color.ORANGERED, Cesium.Color.PLUM,
  Cesium.Color.SPRINGGREEN, Cesium.Color.TOMATO, Cesium.Color.TURQUOISE,
  Cesium.Color.VIOLET, Cesium.Color.YELLOWGREEN, Cesium.Color.SKYBLUE
];

const getUniqueColor = (Cesium, usedColors) => {
  const palette = getColorPalette(Cesium);
  
  // Convert usedColors to comparable format
  const usedColorStrings = usedColors.map(c => {
    if (c instanceof Cesium.Color) return c.toCssColorString();
    return String(c);
  });
  
  // Filter out colors that are already in use
  const availableColors = palette.filter(c => {
    const cssColor = c.toCssColorString();
    return !usedColorStrings.includes(cssColor);
  });
  
  if (availableColors.length === 0) {
    return palette[Math.floor(Math.random() * palette.length)];
  }
  
  return availableColors[Math.floor(Math.random() * availableColors.length)];
};

const VectorCesium = ({ viewer, latestSurvey, apiBase }) => {
  const [layers, setLayers] = useState([]);
  
  // --- CUSTOM POPUP STATE (Leaflet Style) ---
  const [popup, setPopup] = useState(null); 
  const popupRef = useRef(null);      // Reference to the HTML Div
  const popupPosRef = useRef(null);   // Reference to the 3D World Cartesian3 position
  
  const loadedSourcesRef = useRef({}); 
  const loadingRef = useRef({});       
  const isMountedRef = useRef(false);
  const hasZoomedRef = useRef(false);

  // -------------------------
  // 1) DISCOVERY
  // -------------------------
  useEffect(() => {
    if (!viewer || !latestSurvey) return;
    isMountedRef.current = true;

    const discoverLayers = async () => {
      try {
        const p1 = String(latestSurvey.location_name || "").trim();
        const p2 = toDateKey(latestSurvey.survey_time);
        if (!p1 || !p2) return;

        const params = new URLSearchParams({ location_name: p1, survey_time: p2 });
        const discoveryUrl = `${apiBase}/geojson/discovery?${params.toString()}`;
        
        const r = await fetch(discoveryUrl);
        if (!r.ok) return;

        const json = await r.json();
        const candidates = Array.isArray(json.found) ? json.found : [];

        if (candidates.length === 0) {
          if (isMountedRef.current) setLayers([]);
          return;
        }

        const resolved = candidates.map((cand) => {
          const folderName = cand.layer;   
          const fileName = cand.itemname;  
          const url = `${apiBase}/geojson/${encodeURIComponent(p1)}/${encodeURIComponent(p2)}/${encodeURIComponent(folderName)}/${encodeURIComponent(fileName)}.geojson`;

          return {
            id: `vector-${folderName}-${fileName}`,
            name: folderName, 
            title: fileName, 
            url: url,
            visible: true,
            opacity: 1.0,
          };
        });

        if (isMountedRef.current) {
          setLayers(resolved);
          window.dispatchEvent(new CustomEvent("vector-layers-discovered", {
            detail: { location: p1, survey_time: p2, full_layers: resolved }
          }));
        }

      } catch (err) {
        console.warn("[Vector Discovery] Error:", err);
      }
    };

    discoverLayers();
    return () => { isMountedRef.current = false; };
  }, [latestSurvey, apiBase, viewer]);

  // -------------------------
  // 2) LOAD & RENDER
  // -------------------------
  useEffect(() => {
    if (!viewer) return;
    const Cesium = window.Cesium;

    // --- CRASH FIX: Only access infoBox if it exists ---
    if (viewer.infoBox && viewer.infoBox.viewModel) {
        viewer.infoBox.viewModel.showInfo = false;
        const infoContainer = document.querySelector('.cesium-viewer-infoBoxContainer');
        if (infoContainer) infoContainer.style.display = 'none';
    }

    const syncLayers = async () => {
      // Gather currently used colors
      const usedColors = [];
      for (const [id, ds] of Object.entries(loadedSourcesRef.current)) {
        if (ds && ds.entities && ds.entities.values.length > 0) {
          const entity = ds.entities.values[0];
          if (entity.polygon && entity.polygon.material && entity.polygon.material.color) {
            const c = entity.polygon.material.color;
            if (c instanceof Cesium.Color) usedColors.push(c);
          } else if (entity.polyline && entity.polyline.material && entity.polyline.material.color) {
             const c = entity.polyline.material.color;
             if (c instanceof Cesium.Color) usedColors.push(c);
          }
        }
      }

      for (const layer of layers) {
        if (!isMountedRef.current) break;
        const dsId = layer.id;

        if (loadingRef.current[dsId]) continue; 

        let ds = loadedSourcesRef.current[dsId];

        // --- LOAD NEW LAYER ---
        if (!ds) {
            loadingRef.current[dsId] = true;

            try {
                const baseColor = getUniqueColor(Cesium, usedColors);
                usedColors.push(baseColor);
                
                // Load GeoJSON with clampToGround
                ds = await Cesium.GeoJsonDataSource.load(layer.url, {
                    stroke: baseColor,
                    fill: baseColor.withAlpha(0.3),
                    strokeWidth: 3,
                    clampToGround: true, 
                });

                if (viewer.isDestroyed() || !isMountedRef.current) return;

                const entities = ds.entities.values;
                
                for (let i = 0; i < entities.length; i++) {
                    const e = entities[i];

                    // Attach title for our custom Popup handler
                    e._customTitle = layer.title || "Feature Info";

                    // --- B. POLYGON STYLING (Strict Ground Clamping) ---
                    if (e.polygon) {
                        let heightVal = 0;
                        if (e.properties && e.properties.hasProperty("height")) {
                            heightVal = Number(e.properties.height.getValue());
                        }

                        if (heightVal > 0) {
                            // 3D Extrusion
                            e.polygon.extrudedHeight = heightVal;
                            e.polygon.heightReference = Cesium.HeightReference.CLAMP_TO_GROUND;
                            e.polygon.extrudedHeightReference = Cesium.HeightReference.RELATIVE_TO_GROUND;
                            e.polygon.outline = true; 
                        } else {
                            // Flat Ground Clamping
                            e.polygon.extrudedHeight = undefined;
                            e.polygon.outline = false; 
                            e.polygon.perPositionHeight = false; 
                            e.polygon.height = undefined;
                            // This ensures it sticks to Ellipsoid OR Terrain
                            e.polygon.heightReference = Cesium.HeightReference.CLAMP_TO_GROUND;
                        }
                        
                        e.originalColor = e.polygon.material.color instanceof Cesium.Color 
                                            ? e.polygon.material.color : baseColor;
                    }

                    // --- C. POLYLINE STYLING ---
                    if (e.polyline) {
                        e.polyline.clampToGround = true;
                        e.polyline.width = 3;
                        e.polyline.originalColor = e.polyline.material.color instanceof Cesium.Color
                                            ? e.polyline.material.color : baseColor;
                    }
                }

                viewer.dataSources.add(ds);
                loadedSourcesRef.current[dsId] = ds;

            } catch (err) {
                console.warn(`[VectorCesium] Failed load: ${layer.name}`, err);
            } finally {
                delete loadingRef.current[dsId];
            }
        }

        // --- UPDATE VISIBILITY & OPACITY ---
        ds = loadedSourcesRef.current[dsId];
        if (ds && !viewer.isDestroyed()) {
          ds.show = !!layer.visible;
          const alpha = layer.visible ? layer.opacity : 0;
          
          const entities = ds.entities.values;
          for (let i = 0; i < entities.length; i++) {
            const e = entities[i];
            
            if (e.polygon && e.originalColor) {
                let c = e.originalColor;
                if (c instanceof Cesium.ConstantProperty) c = c.getValue();
                e.polygon.material = c.withAlpha(alpha * 0.4); 
            }

            if (e.polyline && e.polyline.originalColor) {
                 let c = e.polyline.originalColor;
                 if (c instanceof Cesium.ConstantProperty) c = c.getValue();
                 e.polyline.material = c.withAlpha(alpha);
            }
          }
        }
      }

      // Initial Zoom
      if (!hasZoomedRef.current && layers.length > 0) {
         const firstId = layers[0].id;
         const firstDs = loadedSourcesRef.current[firstId];
         if (firstDs) {
            viewer.zoomTo(firstDs).then(() => { hasZoomedRef.current = true; }).catch(() => {});
         }
      }
    };

    syncLayers();
  }, [viewer, layers]);

  // -------------------------
  // 3) POPUP LOGIC (Leaflet Style HTML Overlay)
  // -------------------------
  useEffect(() => {
    if (!viewer || viewer.isDestroyed()) return;

    // A. Click Handler (Creates Popup)
    const handler = new window.Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handler.setInputAction((movement) => {
        const pickedObject = viewer.scene.pick(movement.position);
        
        // 1. Check if we clicked a valid vector entity
        if (window.Cesium.defined(pickedObject) && pickedObject.id instanceof window.Cesium.Entity) {
            const entity = pickedObject.id;
            
            // Get precise ground position of click
            const cartesian = viewer.scene.pickPosition(movement.position);
            
            if (cartesian) {
                // Extract properties
                const rawProps = entity.properties ? entity.properties.getValue(window.Cesium.JulianDate.now()) : {};
                const title = entity._customTitle || "Feature Info";
                
                // Store 3D position in Ref for the Render Loop to use
                popupPosRef.current = cartesian;

                // Update React State to render the HTML content
                setPopup({
                    title,
                    data: rawProps
                });
            }
        } else {
            // Clicked empty space -> Close popup
            setPopup(null);
            popupPosRef.current = null;
        }

    }, window.Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // B. Render Loop (Updates Popup Screen Position)
    // This moves the popup div to match the 3D point as the camera moves
    const sceneRenderHandler = viewer.scene.postRender.addEventListener(() => {
        if (popupPosRef.current && popupRef.current) {
            const canvasPosition = window.Cesium.SceneTransforms.wgs84ToWindowCoordinates(
                viewer.scene, 
                popupPosRef.current
            );

            if (canvasPosition) {
                popupRef.current.style.display = 'block';
                // Center bottom alignment (Leaflet style)
                popupRef.current.style.top = `${canvasPosition.y - 10}px`; 
                popupRef.current.style.left = `${canvasPosition.x}px`;
            } else {
                // If point is behind the globe, hide it
                popupRef.current.style.display = 'none';
            }
        }
    });

    return () => {
        if (!handler.isDestroyed()) handler.destroy();
        sceneRenderHandler(); // Remove listener
    };
  }, [viewer]);

  // -------------------------
  // 4) OTHER LISTENERS
  // -------------------------
  useEffect(() => {
    const handleToggle = (ev) => {
      const { id, visible } = ev.detail || {};
      setLayers((prev) => prev.map((l) => 
        (l.id === id || l.name === id) ? { ...l, visible: !!visible } : l
      ));
    };

    const handleOpacity = (ev) => {
      const { id, opacity } = ev.detail || {};
      setLayers((prev) => prev.map((l) => 
        (l.id === id || l.name === id) ? { ...l, opacity: Number(opacity) } : l
      ));
    };

    window.addEventListener("map-toggle-layer", handleToggle);
    window.addEventListener("map-set-opacity", handleOpacity);

    return () => {
      window.removeEventListener("map-toggle-layer", handleToggle);
      window.removeEventListener("map-set-opacity", handleOpacity);
    };
  }, []);

  // -------------------------
  // 5) CLEANUP
  // -------------------------
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      
      if (viewer && !viewer.isDestroyed()) {
        Object.values(loadedSourcesRef.current).forEach((ds) => {
          try { viewer.dataSources.remove(ds, true); } catch (_) {}
        });
      }
      loadedSourcesRef.current = {};
      loadingRef.current = {}; 
      hasZoomedRef.current = false;
    };
  }, [viewer]);

  // -------------------------
  // 6) RENDER POPUP HTML
  // -------------------------
  return (
    <>
      {popup && (
        <div
          ref={popupRef}
          style={{
            position: "absolute",
            zIndex: 9999, // On top of Canvas
            transform: "translate(-50%, -100%)", // Anchor bottom-center
            pointerEvents: "none", // Allow clicks to pass through wrapper
            filter: "drop-shadow(0px 3px 6px rgba(0,0,0,0.3))"
          }}
        >
          {/* Bubble Container */}
          <div style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "0",
            minWidth: "220px",
            maxWidth: "300px",
            pointerEvents: "auto", // Re-enable clicks on content
            fontFamily: "sans-serif",
            fontSize: "12px",
            color: "#333",
            position: "relative"
          }}>
            {/* Close Button */}
            <button 
                onClick={() => { setPopup(null); popupPosRef.current = null; }}
                style={{
                    position: "absolute",
                    top: "5px",
                    right: "5px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#666"
                }}
            >
                ✕
            </button>

            {/* Header */}
            <div style={{
                padding: "10px 12px",
                borderBottom: "1px solid #eee",
                fontWeight: "bold",
                fontSize: "14px",
                marginRight: "20px"
            }}>
                {popup.title}
            </div>

            {/* Scrollable Content Area */}
            <div style={{ padding: "10px 12px", maxHeight: "200px", overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                        {Object.entries(popup.data).map(([key, val]) => (
                            <tr key={key} style={{ borderBottom: "1px solid #f5f5f5" }}>
                                <td style={{ padding: "4px 0", fontWeight: "600", color: "#666", verticalAlign: "top", width: "40%" }}>
                                    {key}
                                </td>
                                <td style={{ padding: "4px 0 4px 8px", color: "#000", wordWrap: "break-word" }}>
                                    {String(val)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {/* Arrow Pointer */}
            <div style={{
                position: "absolute",
                bottom: "-6px",
                left: "50%",
                transform: "translateX(-50%) rotate(45deg)",
                width: "12px",
                height: "12px",
                backgroundColor: "white",
            }} />
          </div>
        </div>
      )}
    </>
  );
};

export default VectorCesium;
